import { Skeleton } from "@klarify/ui/components/ui/skeleton";

/**
 * Placeholder card grid shared by the /therapists Suspense fallback and the
 * directory's in-component searching state, so the two stay visually in sync.
 */
export function TherapistDirectoryCardPlaceholderGrid() {
	return (
		<ul className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
			{Array.from({ length: 6 }, (_, i) => (
				<li key={i}>
					<Skeleton className="h-64 rounded-2xl border border-klarify-neutral-200 bg-white/60" />
				</li>
			))}
		</ul>
	);
}

/**
 * Mirrors FeaturedTherapistCard's structure (identity row, bio lines, pills,
 * post tile, footer) under the same container query, so its height tracks the
 * real card at every width instead of guessing a fixed box.
 */
function FeaturedTherapistCardPlaceholder() {
	return (
		<div className="@container flex flex-col overflow-hidden rounded-2xl border border-klarify-neutral-200 bg-white/60">
			<div className="grid @[34rem]:grid-cols-[minmax(0,1fr)_240px] gap-6 p-6">
				<div className="flex min-w-0 flex-col gap-3">
					<div className="flex items-start gap-3.5">
						<Skeleton className="size-[72px] shrink-0 rounded-[14px]" />
						<div className="flex min-w-0 flex-1 flex-col gap-2 pt-1">
							<Skeleton className="h-5 w-3/4" />
							<Skeleton className="h-3 w-16" />
							<Skeleton className="h-3.5 w-1/2" />
						</div>
					</div>
					<div className="flex flex-col gap-1.5">
						<Skeleton className="h-3.5 w-full" />
						<Skeleton className="h-3.5 w-full" />
						<Skeleton className="h-3.5 w-2/3" />
					</div>
					<div className="flex flex-wrap gap-1.5">
						<Skeleton className="h-6 w-20 rounded-sm" />
						<Skeleton className="h-6 w-24 rounded-sm" />
						<Skeleton className="h-6 w-16 rounded-sm" />
					</div>
				</div>
				<div className="flex flex-col overflow-hidden rounded-lg border border-klarify-neutral-100">
					<Skeleton className="aspect-video w-full rounded-none" />
					<div className="flex flex-col gap-1.5 px-3.5 pt-3 pb-3.5">
						<Skeleton className="h-2.5 w-24" />
						<Skeleton className="h-3.5 w-full" />
						<Skeleton className="h-3.5 w-3/4" />
						<Skeleton className="mt-1.5 h-2.5 w-20" />
					</div>
				</div>
			</div>
			<div className="mt-auto flex gap-2 border-klarify-neutral-100 border-t px-6 py-3">
				<Skeleton className="h-9 w-36 rounded-md" />
				<Skeleton className="h-9 w-28 rounded-md" />
			</div>
		</div>
	);
}

/**
 * Suspense fallback for the /therapists directory. Mirrors the directory's
 * container, header, toolbar, sort bar, card grid, and xl aside column so the
 * swap to real content does not shift layout. The toolbar, sort bar, and
 * aside assume the production steady state (a non-empty directory with
 * clinic posts) — a fallback cannot know emptiness before data resolves.
 */
export function TherapistDirectorySkeleton() {
	return (
		<div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:py-16 xl:max-w-[88rem]">
			<header className="mb-8 flex flex-col gap-3 sm:mb-10">
				<Skeleton className="h-9 w-64 sm:h-10" />
				{/* The description copy wraps to two lines even at max-w-2xl. */}
				<div className="flex max-w-2xl flex-col gap-1">
					<Skeleton className="h-6 w-full" />
					<Skeleton className="h-6 w-2/3" />
				</div>
			</header>
			<div className="mb-6 rounded-2xl border border-klarify-neutral-200 bg-white/70 p-3 sm:mb-8 sm:p-4">
				<div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
					<Skeleton className="h-11 w-full rounded-xl sm:flex-1" />
					<Skeleton className="h-11 w-28 rounded-xl" />
				</div>
			</div>
			<div className="mb-6 sm:mb-8">
				<Skeleton className="mb-3.5 h-4 w-40" />
				<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
					<FeaturedTherapistCardPlaceholder />
					<FeaturedTherapistCardPlaceholder />
				</div>
			</div>
			<div className="xl:grid xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start xl:gap-8">
				<div className="min-w-0">
					<div className="mb-6 flex items-center justify-between gap-3">
						<Skeleton className="h-5 w-32" />
						<Skeleton className="h-5 w-40" />
					</div>
					<TherapistDirectoryCardPlaceholderGrid />
				</div>
				<Skeleton className="hidden h-64 rounded-2xl xl:mt-[45px] xl:block" />
			</div>
		</div>
	);
}
